import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Activity, TrendingUp, Truck, GitCompare, RefreshCw, Lightbulb, ShieldCheck } from "lucide-react";
import { useAuthStore } from "../store/authStore";
import { apiInsights, apiOptimizations } from "../lib/api";

const GREEN = "#075A43";
const ORANGE = "#FF5A1F";
const WINDOWS = [7, 30, 90];

const Metric = ({ label, value, suffix = "", testid }) => (
  <div className="flex items-center justify-between py-1.5" data-testid={testid}>
    <span className="text-sm" style={{ color: "#475569" }}>{label}</span>
    <span className="font-bold" style={{ color: value == null ? "#94a3b8" : "#0b1220" }}>
      {value == null ? "—" : `${value}${suffix}`}
    </span>
  </div>
);

const Insufficient = ({ reason, testid }) => (
  <div className="rounded-lg p-3 text-xs mt-2" style={{ background: "#fff7ed", border: "1px solid #fed7aa", color: "#9a3412" }} data-testid={testid}>
    Données insuffisantes — {reason}
  </div>
);

const Card = ({ icon: Icon, title, children, testid }) => (
  <div className="rounded-2xl bg-white p-5" style={{ border: "1px solid #eef0ef" }} data-testid={testid}>
    <div className="flex items-center gap-2 mb-3">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "#f1f6f4" }}>
        <Icon size={18} color={GREEN} />
      </div>
      <h2 className="font-extrabold" style={{ color: "#0b1220" }}>{title}</h2>
    </div>
    {children}
  </div>
);

export default function InsightsCockpit() {
  const nav = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === "admin";
  const [win, setWin] = useState(7);
  const [data, setData] = useState(null);
  const [opt, setOpt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  const load = useCallback((w) => {
    setLoading(true); setErr(false);
    Promise.all([apiInsights(w), apiOptimizations().catch(() => null)])
      .then(([ins, optimizations]) => { setData(ins); setOpt(optimizations); })
      .catch(() => { setErr(true); toast.error("Chargement du rendement impossible"); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (!isAdmin) { nav("/"); return; } load(win); }, [isAdmin, win, load, nav]);

  if (!isAdmin) return null;

  const f = data?.funnel, d = data?.delivery, ai = data?.ai_divergence, fl = data?.driver_efficiency;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="insights-cockpit">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold" style={{ color: "#0b1220" }}>Rendement TULUSS</h1>
          <p className="text-sm" style={{ color: "#64748b" }}>Analyse en continu des flux réels — aucune donnée inventée.</p>
        </div>
        <div className="flex items-center gap-2">
          {WINDOWS.map((w) => (
            <button key={w} onClick={() => setWin(w)} data-testid={`win-${w}`}
              className="px-3 py-1.5 rounded-full text-sm font-bold"
              style={{ background: win === w ? GREEN : "#fff", color: win === w ? "#fff" : GREEN, border: `1px solid ${GREEN}` }}>
              {w} j
            </button>
          ))}
          <button onClick={() => load(win)} data-testid="insights-refresh" className="p-2 rounded-full" style={{ color: ORANGE }} title="Rafraîchir">
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 gap-4">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-48 rounded-2xl animate-pulse" style={{ background: "#eef0ef" }} />)}
        </div>
      ) : err ? (
        <div className="text-center py-20" style={{ color: "#64748b" }} data-testid="insights-error">
          <p className="font-semibold mb-3">Impossible de charger les indicateurs.</p>
          <button onClick={() => load(win)} className="font-bold" style={{ color: ORANGE }}>Réessayer</button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          <Card icon={Activity} title="Funnel client" testid="card-funnel">
            {f?.status === "ok" ? (
              <>
                <Metric label="Vues produit" value={f.events.product_view} testid="funnel-views" />
                <Metric label="Ajouts panier" value={f.events.cart_add} testid="funnel-carts" />
                <Metric label="Checkouts" value={f.events.checkout_start} testid="funnel-checkouts" />
                <Metric label="Vue → panier" value={f.rates_pct.view_to_cart} suffix=" %" testid="funnel-v2c" />
                <Metric label="Panier → checkout" value={f.rates_pct.cart_to_checkout} suffix=" %" testid="funnel-c2co" />
                <Metric label="Paiements (fenêtre)" value={f.paid_orders} testid="funnel-paid" />
                <Metric label="Click & Collect" value={f.click_collect_orders} testid="funnel-cc" />
              </>
            ) : <Insufficient reason={f?.reason} testid="funnel-insufficient" />}
          </Card>

          <Card icon={Truck} title="Livraison" testid="card-delivery">
            {d?.status === "ok" ? (
              <>
                <Metric label="Taux de succès" value={d.success_rate_pct} suffix=" %" testid="delivery-success" />
                <Metric label="Outcomes mesurés" value={d.delivery_outcomes} testid="delivery-outcomes" />
                <Metric label="Changements de statut" value={d.status_changes} testid="delivery-status" />
                <Metric label="Mises à jour ETA" value={d.eta_updates} testid="delivery-eta" />
                <Insufficient reason={d.eta_error_p50_p80_p95.reason} testid="delivery-eta-insufficient" />
              </>
            ) : <Insufficient reason={d?.reason} testid="delivery-insufficient" />}
          </Card>

          <Card icon={GitCompare} title="Divergence Gemini ↔ TYLUSS" testid="card-ai">
            {ai?.status === "ok" ? (
              <>
                <Metric label="Comparaisons" value={ai.comparisons} testid="ai-count" />
                <Metric label="Divergence moyenne" value={ai.avg_divergence} testid="ai-div" />
                <Metric label="Couverture Gemini" value={ai.avg_coverage_gemini} testid="ai-cov-g" />
                <Metric label="Couverture TYLUSS" value={ai.avg_coverage_tyluss} testid="ai-cov-t" />
                <Metric label="Tendance" value={ai.trend_divergence} testid="ai-trend" />
              </>
            ) : <Insufficient reason={ai?.reason} testid="ai-insufficient" />}
          </Card>

          <Card icon={TrendingUp} title="Efficacité flotte" testid="card-fleet">
            {fl?.status === "ok" ? (
              <>
                <Metric label="Regroupements" value={fl.regroupements} testid="fleet-fused" />
                <Metric label="Taille moy. groupe" value={fl.avg_group_size} testid="fleet-group" />
                <Metric label="Échantillons GPS" value={fl.gps_samples} testid="fleet-gps" />
                <Metric label="Incidents" value={fl.incidents} testid="fleet-incidents" />
                <Insufficient reason={fl.km_a_vide.reason} testid="fleet-kmvide-insufficient" />
              </>
            ) : <Insufficient reason={fl?.reason} testid="fleet-insufficient" />}
          </Card>
        </div>
      )}

      {!loading && !err && opt && (
        <div className="mt-8" data-testid="optimizations-section">
          <div className="flex items-center gap-2 mb-1">
            <Lightbulb size={20} color={ORANGE} />
            <h2 className="text-xl font-extrabold" style={{ color: "#0b1220" }}>Optimisations à valider</h2>
          </div>
          <p className="text-sm mb-4" style={{ color: "#64748b" }}>
            TULUSS propose, le Founder décide. Aucun paramètre VORTEX n&apos;est modifié automatiquement.
          </p>

          {opt.recommendations?.length ? (
            <div className="grid gap-3">
              {opt.recommendations.map((r, i) => (
                <div key={r.domain} className="rounded-2xl bg-white p-5" style={{ border: `1px solid ${i === 0 ? ORANGE : "#eef0ef"}` }} data-testid={`opt-rec-${r.domain}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-[220px]">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-extrabold" style={{ color: "#0b1220" }}>{r.domain}</span>
                        {i === 0 && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#fff7ed", color: "#9a3412" }}>
                            IMPACT PRIORITAIRE
                          </span>
                        )}
                        {r.low_confidence && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#eff6ff", color: "#1d4ed8" }} data-testid={`opt-lowconf-${r.domain}`}>
                            ÉCHANTILLON LIMITÉ
                          </span>
                        )}
                      </div>
                      <p className="text-sm mb-2" style={{ color: "#334155" }} data-testid={`opt-insight-${r.domain}`}>{r.insight}</p>
                      <p className="text-xs" style={{ color: "#64748b" }}>→ {r.suggested_action}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-extrabold" style={{ color: ORANGE }} data-testid={`opt-error-${r.domain}`}>{r.avg_error}</div>
                      <div className="text-[11px]" style={{ color: "#94a3b8" }}>erreur moy.</div>
                      <div className="text-[11px] mt-1" style={{ color: "#94a3b8" }}>{r.observations} obs.</div>
                    </div>
                  </div>
                  <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full" style={{ background: "#f1f6f4", color: GREEN }} data-testid={`opt-status-${r.domain}`}>
                    <ShieldCheck size={13} /> En attente de validation Founder
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Insufficient reason={opt.reason} testid="opt-insufficient" />
          )}

          {opt.stable_domains?.length > 0 && (
            <div className="mt-4 rounded-2xl p-4" style={{ background: "#f8fafc", border: "1px solid #eef0ef" }} data-testid="opt-stable">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck size={16} color={GREEN} />
                <span className="text-sm font-bold" style={{ color: "#0b1220" }}>Domaines stables (sous surveillance)</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {opt.stable_domains.map((s) => (
                  <span key={s.domain} className="text-xs px-2.5 py-1 rounded-full" style={{ background: "#fff", border: "1px solid #e2e8f0", color: "#475569" }} data-testid={`opt-stable-${s.domain}`}>
                    {s.domain} · {s.observations} obs · écart {s.avg_error == null ? "—" : s.avg_error}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {data?.generated_at && (
        <p className="text-xs mt-6 text-center" style={{ color: "#94a3b8" }} data-testid="insights-generated">
          Fenêtre {data.window_days} j — généré {new Date(data.generated_at).toLocaleString("fr-CH")}
        </p>
      )}
    </div>
  );
}
